import { ModuleColumnEnum, TextTypeEnum } from 'generated/graphql';
import type { ModuleDataColumn } from '../types';

type SpreadColumn = Pick<ModuleDataColumn, 'type' | 'textType'>;

const isMediaColumn = (column: SpreadColumn): boolean =>
    column.type === ModuleColumnEnum.ImageColumn ||
    column.type === ModuleColumnEnum.VideoColumn;

const isBodyColumn = (column: SpreadColumn): boolean =>
    column.type === ModuleColumnEnum.TextColumn &&
    column.textType !== TextTypeEnum.H3;

/**
 * Whether a picture + copy spread stands its picture on the right.
 *
 * The stored column order decides the side, and the Swap action is what
 * writes it: copy stored ahead of the picture puts the picture on the right.
 * Only the body copy counts — the optional h3 title rides above the body
 * wherever the body goes, so a title stored ahead of the picture moves
 * nothing.
 */
export const isSpreadMediaOnRight = (columns: SpreadColumn[]): boolean => {
    const mediaIndex = columns.findIndex(isMediaColumn);
    const bodyIndex = columns.findIndex(isBodyColumn);
    return mediaIndex >= 0 && bodyIndex >= 0 && bodyIndex < mediaIndex;
};

/**
 * The column order the Swap action writes.
 *
 * A spread with a single picture moves that picture to the other side of its
 * copy and leaves the copy's own order alone, so a title and two body columns
 * stay title, first, second. Anything else — a pair of photos — trades its
 * first two columns.
 */
export const swapModuleColumns = <T extends SpreadColumn>(
    columns: T[],
): T[] => {
    const media = columns.filter(isMediaColumn);
    const [picture] = media;
    if (media.length !== 1 || !picture) {
        const [first, second, ...rest] = columns;
        return first && second ? [second, first, ...rest] : columns;
    }
    const copy = columns.filter((column) => column !== picture);
    return isSpreadMediaOnRight(columns)
        ? [picture, ...copy]
        : [...copy, picture];
};
